import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';

import { BaseSelectFilter } from '@/components/common';

import { useGetTemperaturesQuery } from '@/graphql/GetTemperatures.generated';

import { appStore, reportRequestError, retryAfterRequestError } from '@/store';

import {
  ControlledAtmosphereTemperaturesFilter,
  getTemperature,
  selectAllTemperatures,
  setSelectedTemperatures,
  setTemperatures,
  store,
  unselectAllTemperatures,
  unselectTemperature,
} from '../store';

const TemperaturesSelectFilter = () => {
  const appSnap = useSnapshot(appStore);
  const snap = useSnapshot(store);

  const [{ data, fetching, error }, reexecuteQuery] = useGetTemperaturesQuery({
    pause: snap.temperatures.length > 0,
  });

  const [errorId, setErrorId] = useState<string | null>(null);

  useEffect(() => {
    if (data && data.container_insights_temperatures) {
      setTemperatures([
        ...data.container_insights_temperatures,
        ControlledAtmosphereTemperaturesFilter,
      ]);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setErrorId(reportRequestError(error, reexecuteQuery));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = () => {
    retryAfterRequestError(errorId);
    setErrorId(null);
  };

  const handleRemove = (id: string) => () => unselectTemperature(id);

  const selectedTemperaturesIds = snap.selectedTemperatures.map(
    (temperature) => temperature.id,
  );

  const allSelected =
    selectedTemperaturesIds.length === snap.temperatures.length;

  const controlledAtmosphereItem = snap.temperatures.find(
    (temperature) =>
      temperature.id === ControlledAtmosphereTemperaturesFilter.id,
  );

  const { t } = useTranslation();

  return (
    <BaseSelectFilter
      maxHeight={48 * 8 + 8}
      id="temperatures-select-filter"
      label={t('filters.temperatures')}
      values={selectedTemperaturesIds}
      onChange={setSelectedTemperatures}
      onClear={unselectAllTemperatures}
      loading={fetching}
      error={
        appSnap.hasRequestError(errorId)
          ? 'Error while fetching temperatures'
          : null
      }
      onRetry={handleRetry}
      renderChips={(selected: string[]) => (
        <>
          {selected
            .map(getTemperature)
            .filter((temperature) => temperature !== null)
            .map((temperature) => (
              <Chip
                key={temperature.id}
                color="primary"
                variant="outlined"
                label={temperature.label}
                onDelete={handleRemove(temperature.id)}
              />
            ))}
        </>
      )}
    >
      <ListSubheader
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          Temperatures
          <sup
            style={{
              marginLeft: 4,
              visibility:
                selectedTemperaturesIds.length > 0 ? 'visible' : 'hidden',
            }}
          >
            ({selectedTemperaturesIds.length})
          </sup>
        </Box>
        {snap.temperatures.length > 0 && (
          <Button
            sx={{ textTransform: 'none' }}
            onClick={
              allSelected ? unselectAllTemperatures : selectAllTemperatures
            }
          >
            {allSelected ? 'Clear' : 'Select all'}
          </Button>
        )}
      </ListSubheader>
      {snap.temperatures
        .filter(
          (temperature) =>
            temperature.id !== ControlledAtmosphereTemperaturesFilter.id,
        )
        .map((temperature) => (
          <MenuItem key={temperature.id} value={temperature.id}>
            <Checkbox checked={temperature.selected} />
            <ListItemText primary={temperature.label} />
          </MenuItem>
        ))}
      <Divider />
      {controlledAtmosphereItem && (
        <MenuItem
          key={controlledAtmosphereItem.id}
          value={controlledAtmosphereItem.id}
        >
          <Switch checked={controlledAtmosphereItem.selected} />
          <ListItemText primary={controlledAtmosphereItem.label} />
        </MenuItem>
      )}
    </BaseSelectFilter>
  );
};

export { TemperaturesSelectFilter };
